/**
 * Música y ambiente sintetizados con Web Audio (composiciones originales, sin
 * archivos). Un secuenciador con planificación anticipada (lookahead) toca la
 * pista actual compás a compás; cambiar de pista hace un fundido cruzado.
 *
 * - `menu`: tema tranquilo en re menor (pads, arpegio y campanas).
 * - `match`: tema de aventura en la menor; la capa de batalla (batería completa
 *   y melodía) sube con `setIntensity` cuando hay combate cerca.
 * - Ambiente de partida: pájaros de vez en cuando (sin ruido de fondo continuo:
 *   el viento filtrado sonaba a cascada).
 */

export type MusicTrack = 'menu' | 'match' | 'none';

const LOOKAHEAD = 0.18;
const TICK_MS = 30;

/** [paso de semicorchea dentro del compás, nota MIDI, duración en semicorcheas] */
type Note = [number, number, number];

interface TrackDef {
  bpm: number;
  /** acordes por compás (notas MIDI) */
  chords: number[][];
  /** bajo: nota MIDI por compás */
  bass: number[];
  /** melodía por compás */
  melody: Note[][];
  /** compases en los que suena la melodía (el resto sólo acompañamiento) */
  melodyBars: (bar: number) => boolean;
}

const midi = (n: number): number => 440 * 2 ** ((n - 69) / 12);

// re menor: Rem – Sib – Fa – Do
const MENU: TrackDef = {
  bpm: 80,
  chords: [
    [62, 65, 69], [58, 62, 65], [65, 69, 72], [60, 64, 67],
    [62, 65, 69], [58, 62, 65], [65, 69, 72], [60, 64, 67],
  ],
  bass: [38, 34, 41, 36, 38, 34, 41, 36],
  melody: [
    [], [], [], [],
    [[0, 81, 6], [6, 77, 2], [8, 74, 8]],
    [[0, 77, 6], [6, 79, 2], [8, 81, 8]],
    [[0, 84, 4], [4, 81, 4], [8, 77, 8]],
    [[0, 76, 6], [6, 79, 2], [8, 79, 8]],
  ],
  melodyBars: (bar) => bar >= 4,
};

// la menor: Lam – Fa – Do – Sol | Lam – Fa – Sol – Mi
const MATCH: TrackDef = {
  bpm: 132,
  chords: [
    [57, 60, 64], [53, 57, 60], [60, 64, 67], [55, 59, 62],
    [57, 60, 64], [53, 57, 60], [55, 59, 62], [52, 56, 59],
  ],
  bass: [33, 29, 36, 31, 33, 29, 31, 28],
  melody: [
    [[0, 76, 4], [4, 74, 2], [6, 72, 2], [8, 71, 4], [12, 72, 4]],
    [[0, 69, 6], [6, 72, 2], [8, 77, 6], [14, 76, 2]],
    [[0, 76, 4], [4, 79, 4], [8, 76, 2], [10, 74, 2], [12, 72, 4]],
    [[0, 74, 8], [8, 71, 4], [12, 67, 4]],
    [[0, 69, 2], [2, 72, 2], [4, 76, 4], [8, 81, 6], [14, 79, 2]],
    [[0, 77, 4], [4, 76, 4], [8, 72, 4], [12, 69, 4]],
    [[0, 71, 4], [4, 74, 4], [8, 79, 4], [12, 77, 4]],
    [[0, 76, 8], [8, 68, 4], [12, 71, 4]],
  ],
  melodyBars: () => true,
};

const TRACKS: Record<Exclude<MusicTrack, 'none'>, TrackDef> = { menu: MENU, match: MATCH };

interface Playing {
  name: Exclude<MusicTrack, 'none'>;
  def: TrackDef;
  gain: GainNode;
  /** capa de batalla (batería completa + melodía), sigue a la intensidad */
  battle: GainNode;
  nextStep: number;
  step: number;
  stopAt: number;
}

export class Music {
  private readonly musicBus: GainNode;
  private readonly ambientBus: GainNode;
  private playing?: Playing;
  private fading: Playing[] = [];
  private track: MusicTrack = 'none';
  private intensity = 0;
  private timer = 0;
  private ambientOn = false;
  private ambientNodes: AudioScheduledSourceNode[] = [];
  private ambientGain?: GainNode;
  private nextBirdAt = 0;

  constructor(
    private readonly ctx: AudioContext,
    musicOut: AudioNode,
    ambientOut: AudioNode,
    private readonly noise: AudioBuffer,
  ) {
    this.musicBus = ctx.createGain();
    this.musicBus.connect(musicOut);
    this.ambientBus = ctx.createGain();
    this.ambientBus.connect(ambientOut);
    this.timer = window.setInterval(() => this.schedule(), TICK_MS);
  }

  dispose(): void {
    window.clearInterval(this.timer);
  }

  setTrack(track: MusicTrack): void {
    if (track === this.track) return;
    this.track = track;
    const now = this.ctx.currentTime;
    if (this.playing) {
      const old = this.playing;
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setValueAtTime(old.gain.gain.value, now);
      old.gain.gain.linearRampToValueAtTime(0, now + 1.4);
      old.stopAt = now + 1.5;
      this.fading.push(old);
      this.playing = undefined;
    }
    if (track === 'none') return;
    const def = TRACKS[track];
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + 1.2);
    gain.connect(this.musicBus);
    const battle = this.ctx.createGain();
    battle.gain.value = this.intensity;
    battle.connect(gain);
    this.playing = { name: track, def, gain, battle, nextStep: now + 0.1, step: 0, stopAt: Infinity };
  }

  /** 0 = calma, 1 = combate: sube la capa de batalla de la música de partida. */
  setIntensity(value: number): void {
    const target = Math.max(0, Math.min(1, value));
    if (Math.abs(target - this.intensity) < 0.01) return;
    this.intensity = target;
    const battle = this.playing?.battle;
    if (battle) battle.gain.setTargetAtTime(target, this.ctx.currentTime, 0.8);
  }

  /** Pájaros de fondo durante la partida. */
  setAmbient(on: boolean): void {
    if (on === this.ambientOn) return;
    this.ambientOn = on;
    const now = this.ctx.currentTime;
    if (!on) {
      if (this.ambientGain) {
        const g = this.ambientGain;
        g.gain.cancelScheduledValues(now);
        g.gain.setValueAtTime(g.gain.value, now);
        g.gain.linearRampToValueAtTime(0, now + 1);
      }
      for (const node of this.ambientNodes) {
        try { node.stop(now + 1.1); } catch { /* ya parado */ }
      }
      this.ambientNodes = [];
      this.ambientGain = undefined;
      return;
    }
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(1, now + 2);
    g.connect(this.ambientBus);
    this.ambientGain = g;
    this.nextBirdAt = now + 2 + Math.random() * 4;
  }

  private schedule(): void {
    const now = this.ctx.currentTime;
    if (this.ctx.state !== 'running') return;
    this.fading = this.fading.filter((p) => {
      if (now < p.stopAt) {
        this.scheduleTrack(p, now);
        return true;
      }
      p.gain.disconnect();
      return false;
    });
    if (this.playing) this.scheduleTrack(this.playing, now);
    if (this.ambientOn && this.ambientGain && now >= this.nextBirdAt) {
      this.bird(now + 0.05);
      this.nextBirdAt = now + 3.5 + Math.random() * 7;
    }
  }

  private scheduleTrack(p: Playing, now: number): void {
    const stepDur = 60 / p.def.bpm / 4;
    // si la pestaña estuvo en segundo plano, no intentes ponerte al día
    if (p.nextStep < now - 0.5) p.nextStep = now + 0.05;
    while (p.nextStep < now + LOOKAHEAD && p.nextStep < p.stopAt) {
      this.playStep(p, p.step, p.nextStep, stepDur);
      p.step = (p.step + 1) % (p.def.chords.length * 16);
      p.nextStep += stepDur;
    }
  }

  private playStep(p: Playing, step: number, t: number, sd: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const chord = p.def.chords[bar];
    const out = p.gain;
    if (p.name === 'menu') {
      if (s === 0) {
        this.pad(out, chord, t, sd * 16, 0.05);
        this.bass(out, p.def.bass[bar], t, sd * 14, 0.11, 'sine');
      }
      // arpegio en corcheas
      if (s % 2 === 0) {
        const pattern = [0, 1, 2, 1, 3, 2, 1, 2];
        const idx = pattern[(s / 2) % 8];
        const note = idx === 3 ? chord[0] + 12 : chord[idx];
        this.pluck(out, note + 12, t, sd * 3, 0.045);
      }
      if (p.def.melodyBars(bar)) {
        for (const [at, note, len] of p.def.melody[bar]) if (at === s) this.bell(out, note, t, sd * len, 0.07);
      }
      return;
    }

    // partida: acompañamiento siempre, batería completa y melodía según intensidad
    if (s === 0) this.pad(out, chord, t, sd * 16, 0.028);
    if (s % 2 === 0) {
      const octave = s % 4 === 2 ? 12 : 0;
      this.bass(out, p.def.bass[bar] + octave, t, sd * 1.8, 0.13, 'square');
    }
    if (s % 4 === 0) this.pluck(out, chord[(s / 4) % 3] + 12, t, sd * 2, 0.03);
    // pulso suave también en calma
    if (s === 0 || s === 8) this.kick(out, t, 0.32);
    if (s % 4 === 2) this.hat(out, t, 0.025);
    const battle = p.battle;
    if (s === 4 || s === 12) this.snare(battle, t, 0.3);
    if (s === 10) this.kick(battle, t, 0.25);
    if (s % 2 === 1) this.hat(battle, t, 0.03);
    if (s % 2 === 0) this.pluck(battle, chord[(s / 2) % 3] + 24, t, sd * 1.5, 0.025);
    for (const [at, note, len] of p.def.melody[bar]) if (at === s) this.lead(battle, note, t, sd * len, 0.06);
  }

  // ------------------------------------------------------------ instrumentos

  private env(out: AudioNode, t: number, attack: number, hold: number, release: number, gain: number): GainNode {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + attack);
    g.gain.setValueAtTime(gain, t + Math.max(attack, hold));
    g.gain.exponentialRampToValueAtTime(0.0001, t + Math.max(attack, hold) + release);
    g.connect(out);
    return g;
  }

  private osc(type: OscillatorType, freq: number, t: number, end: number, dest: AudioNode, detune = 0): OscillatorNode {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.detune.value = detune;
    o.connect(dest);
    o.start(t);
    o.stop(end + 0.05);
    return o;
  }

  private pad(out: AudioNode, chord: number[], t: number, dur: number, gain: number): void {
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1100;
    const g = this.env(filter, t, Math.min(0.6, dur * 0.3), dur * 0.75, dur * 0.35, gain);
    void g;
    filter.connect(out);
    for (const note of chord) {
      this.osc('sawtooth', midi(note), t, t + dur * 1.1, g, -7);
      this.osc('sawtooth', midi(note), t, t + dur * 1.1, g, 7);
    }
  }

  private pluck(out: AudioNode, note: number, t: number, dur: number, gain: number): void {
    const g = this.env(out, t, 0.004, 0.01, dur, gain);
    this.osc('triangle', midi(note), t, t + dur + 0.02, g);
    const g2 = this.env(out, t, 0.004, 0.01, dur * 0.5, gain * 0.35);
    this.osc('sine', midi(note + 12), t, t + dur, g2);
  }

  private bell(out: AudioNode, note: number, t: number, dur: number, gain: number): void {
    const len = Math.max(0.6, dur * 1.2);
    const g = this.env(out, t, 0.006, 0.02, len, gain);
    this.osc('sine', midi(note), t, t + len + 0.05, g);
    const g2 = this.env(out, t, 0.004, 0.01, len * 0.35, gain * 0.4);
    this.osc('sine', midi(note) * 2.76, t, t + len * 0.4, g2);
  }

  private bass(out: AudioNode, note: number, t: number, dur: number, gain: number, type: OscillatorType): void {
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = type === 'square' ? 520 : 900;
    filter.connect(out);
    const g = this.env(filter, t, 0.006, dur * 0.6, dur * 0.4, gain);
    this.osc(type, midi(note), t, t + dur, g);
  }

  private lead(out: AudioNode, note: number, t: number, dur: number, gain: number): void {
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 2600;
    filter.connect(out);
    const g = this.env(filter, t, 0.02, dur * 0.8, dur * 0.3 + 0.05, gain);
    const o = this.osc('square', midi(note), t, t + dur * 1.15, g);
    const vib = this.ctx.createOscillator();
    vib.frequency.value = 5.5;
    const depth = this.ctx.createGain();
    depth.gain.value = midi(note) * 0.006;
    vib.connect(depth).connect(o.frequency);
    vib.start(t + 0.12);
    vib.stop(t + dur * 1.15 + 0.05);
    const g2 = this.env(out, t, 0.02, dur * 0.8, dur * 0.3 + 0.05, gain * 0.5);
    this.osc('triangle', midi(note), t, t + dur * 1.15, g2, 6);
  }

  private kick(out: AudioNode, t: number, gain: number): void {
    const g = this.env(out, t, 0.002, 0.01, 0.22, gain);
    const o = this.osc('sine', 120, t, t + 0.25, g);
    o.frequency.setValueAtTime(120, t);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.18);
  }

  private noiseHit(out: AudioNode, t: number, type: BiquadFilterType, freq: number, dur: number, gain: number): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    const g = this.env(out, t, 0.002, 0.005, dur, gain);
    src.connect(filter).connect(g);
    src.start(t, Math.random() * 1.5);
    src.stop(t + dur + 0.05);
  }

  private snare(out: AudioNode, t: number, gain: number): void {
    this.noiseHit(out, t, 'bandpass', 1800, 0.16, gain);
    const g = this.env(out, t, 0.002, 0.005, 0.08, gain * 0.4);
    this.osc('triangle', 190, t, t + 0.1, g);
  }

  private hat(out: AudioNode, t: number, gain: number): void {
    this.noiseHit(out, t, 'highpass', 7500, 0.045, gain);
  }

  /** Trino de pájaro: dos a cuatro silbidos rápidos que suben y bajan. */
  private bird(t: number): void {
    if (!this.ambientGain) return;
    const chirps = 2 + Math.floor(Math.random() * 3);
    const base = 2300 + Math.random() * 1400;
    const pan = this.ctx.createStereoPanner();
    pan.pan.value = Math.random() * 1.4 - 0.7;
    pan.connect(this.ambientGain);
    for (let i = 0; i < chirps; i++) {
      const at = t + i * (0.09 + Math.random() * 0.05);
      const g = this.env(pan, at, 0.01, 0.02, 0.07, 0.035);
      const o = this.osc('sine', base, at, at + 0.1, g);
      o.frequency.setValueAtTime(base, at);
      o.frequency.exponentialRampToValueAtTime(base * (1.25 + Math.random() * 0.3), at + 0.04);
      o.frequency.exponentialRampToValueAtTime(base * 0.9, at + 0.09);
    }
  }
}
